import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import { getApiErrorMessage } from '@/lib/api/client';
import { getData, getPaginated, postData, postFormData } from '@/lib/api/client-v2';
import { DATE_ISO, formatDate, type DateInput } from '@/lib/dates';
import { notify } from '@/lib/notify';
import { queryKeys } from '@/lib/query-keys';
import { ENDPOINTS, PDF } from '@/types/api/endpoints';
import type { FleetList, FleetVehicle, FleetVehicleV2 } from '@/types/api/fleet';

/*
 * Flotte (contrats COOLTRA) : reprise de l’ancien middleware `flotte`, sur l'API v2
 * (`/contracts/{id}/fleet`).
 */

type Id = string | number;

/**
 * Véhicule v2 ramené aux noms lus par les écrans (ceux de la v1). `contrat` n'est plus servi : il
 * vient de l'URL. Le mémo garde le lien du Pass quand il est servi, sinon le PDF v2.
 */
export const toFleetVehicle = (vehicle: FleetVehicleV2, contractId: Id): FleetVehicle => ({
    adhesion: vehicle.adhesion,
    contrat: contractId,
    marque: vehicle.brand ?? undefined,
    modele: vehicle.model ?? undefined,
    immatriculation: vehicle.plate ?? undefined,
    cylindree: vehicle.cylinder ?? undefined,
    dateMEC: vehicle.first_registration_date ?? undefined,
    dateEntree: vehicle.entry_date ?? undefined,
    dateSortie: vehicle.exit_date ?? undefined,
    inactif: vehicle.inactive,
    leasing: vehicle.leasing,
    // `memo_link` de la ressource pointe sur la route v1 du mémo : on prend celle de la v2.
    memo_link: PDF.fleetVehicleMemo(vehicle.adhesion),
    garanties: (vehicle.guarantees ?? []).map((guarantee) => ({
        libelle: guarantee.label ?? undefined,
        code_famille: guarantee.family_code ?? undefined,
    })),
});

export interface FleetFilters {
    /** Page demandée (numéro), la première par défaut. */
    page?: number;
    sort?: string;
    immat?: string;
}

/**
 * Véhicules d'une flotte, page et filtres compris.
 *
 * Relue à chaque montage, comme avant ; la liste précédente reste affichée pendant le
 * chargement d'une autre page ou d'un autre filtre (ancien état `flotte`). Le filtre `active` de
 * la v1 n'est pas repris : aucun écran ne le renseignait, et la v2 ne l'expose pas.
 */
export const useFleet = (contractId: Id, { page, sort, immat }: FleetFilters = {}) => {
    const params = Object.fromEntries(
        Object.entries({ page, sort, immat }).filter(([, value]) => value !== undefined && value !== null && value !== ''),
    );

    return useQuery({
        queryKey: queryKeys.fleet(`${ENDPOINTS.fleet(contractId)}?${new URLSearchParams(Object.entries(params).map(([key, value]) => [key, String(value)])).toString()}`),
        queryFn: async (): Promise<FleetList> => {
            const { data, meta } = await getPaginated<FleetVehicleV2[]>(ENDPOINTS.fleet(contractId), params);

            return {
                data: (data ?? []).map((vehicle) => toFleetVehicle(vehicle, contractId)),
                meta: {
                    total: meta.total ?? undefined,
                    per_page: meta.per_page ?? undefined,
                    current_page: meta.current_page ?? undefined,
                    last_page: meta.last_page ?? undefined,
                },
            };
        },
        placeholderData: keepPreviousData,
    });
};

/** Détail d'un véhicule de la flotte (`GET /contracts/{id}/fleet/{adhesion}`), relu à chaque montage. */
export const useFleetVehicle = (contractId: Id, adhesion: Id) =>
    useQuery({
        queryKey: queryKeys.fleetVehicle(ENDPOINTS.fleetVehicle(contractId, adhesion)),
        queryFn: async () =>
            toFleetVehicle(await getData<FleetVehicleV2>(ENDPOINTS.fleetVehicle(contractId, adhesion)), contractId),
    });

export interface DeleteVehiclesForm {
    contract_id?: string | number;
    selected_vehicules?: (string | number)[];
    date_sortie?: DateInput;
}

/**
 * Sortie de véhicules de la flotte (`POST /contracts/{id}/fleet/exit`) : `adhesions[]` (ex
 * `selected_vehicules`) et `date_sortie` en `yyyy-MM-dd`, transmise telle quelle à ABS comme en v1.
 */
export const useDeleteVehicles = () =>
    useMutation({
        mutationFn: ({ form }: { form: DeleteVehiclesForm; successFunction?: () => void }) =>
            postData<null>(ENDPOINTS.fleetExit(form.contract_id ?? ''), {
                date_sortie: formatDate(form.date_sortie, DATE_ISO),
                adhesions: form.selected_vehicules ?? [],
            }),
        onSuccess: (_data, { successFunction }) => {
            void notify({
                html: '<p>La sortie des véhicules de la flotte a été effectuée</p>',
                icon: 'success',
                title: 'Suppression effectuée !',
                confirmButtonText: "J'ai compris",
                allowOutsideClick: false,
                allowEscapeKey: false,
                successFunction,
            });
        },
        onError: (error) => {
            void notify({
                html: `<p>Votre demande n'a pas pu être envoyée. ${getApiErrorMessage(error)}</p>`,
                icon: 'error',
                title: 'Erreur lors de la suppression !',
            });
        },
    });

export interface AddVehicleForm {
    /** Contrat flotte : il part dans l'URL, plus dans le corps. */
    id?: string | number;
    date_mec?: DateInput;
    date_achat?: DateInput;
}

/**
 * Entrée d'un véhicule dans la flotte (`POST /contracts/{id}/fleet`), en multipart : dates au
 * format `dd/MM/yyyy`, sans les champs de sélection (`type`, `cylindree`, `vehicule`), sans les
 * valeurs nulles. Le contrat (`id`) passe dans l'URL.
 */
export const useAddVehicle = () =>
    useMutation({
        mutationFn: ({ form }: { form: AddVehicleForm; successFunction?: () => void }) => {
            /* Copie de surface : seules les clés de premier niveau sont modifiées. */
            const cloneAdd: Record<string, unknown> = { ...form };
            cloneAdd.date_mec = formatDate(form.date_mec);
            cloneAdd.date_achat = formatDate(form.date_achat);

            delete cloneAdd.id;
            delete cloneAdd.type;
            delete cloneAdd.cylindree;
            delete cloneAdd.vehicule;

            const dataForm = new FormData();
            for (const [key, value] of Object.entries(cloneAdd)) {
                if (value !== undefined && value !== null) {
                    dataForm.append(key, value instanceof Blob ? value : String(value));
                }
            }

            return postFormData<null>(ENDPOINTS.fleet(form.id ?? ''), dataForm);
        },
        onSuccess: (_data, { successFunction }) => {
            void notify({
                html: "<p>La demande a bien été enregistrée, les informations seront visibles d'ici 24h.</p>",
                icon: 'success',
                title: 'Véhicule enregistré !',
                confirmButtonText: "J'ai compris",
                successFunction,
            });
        },
        onError: (error) => {
            void notify({
                html: `<p>Votre demande n'a pas pu être envoyée. ${getApiErrorMessage(error)}</p>`,
                icon: 'error',
                title: "Erreur lors de l'ajout du véhicule !",
            });
        },
    });
